/**
 * Bilingual language toggle. A single preference ("zh" or "en") that
 * decides which side of every `<BilingualText>` shows as the primary
 * copy. Persists in `localStorage["tw.lang"]` so a refresh keeps state.
 *
 * Components subscribe via `subscribeLang(cb)`. The toggle widget in
 * the sidebar calls `setLang()`.
 */

export type Lang = "zh" | "en";

const STORAGE_KEY = "tw.lang";
const DEFAULT_LANG: Lang = "zh";

let current: Lang = read();
const listeners = new Set<(next: Lang) => void>();

export function getLang(): Lang {
  return current;
}

export function setLang(next: Lang): void {
  if (next !== "zh" && next !== "en") return;
  if (next === current) return;
  current = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Privacy mode etc — keep the in-memory value, ignore persistence.
  }
  for (const cb of listeners) cb(current);
}

export function toggleLang(): Lang {
  setLang(current === "zh" ? "en" : "zh");
  return current;
}

export function subscribeLang(cb: (lang: Lang) => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function read(): Lang {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "zh" || raw === "en") return raw;
  } catch {
    // ignore
  }
  return DEFAULT_LANG;
}
